<script setup>

import {useRouter} from "vue-router";
import {computed, reactive, watch} from "vue";
import LoginModal from "@/Modals/LoginModal.vue";
import RegistrationModal from "@/Modals/RegistrationModal.vue";
import {useUserStore} from "@/Stores/userStore";
import {useFeatureStore} from "@/Stores/featureStore";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import EmailPreferencesModal from "@/Modals/EmailPreferencesModal.vue";
import NetPromoterModal from "@/Modals/NetPromoterModal.vue";
import MagicalMistBackground from "@/Components/MagicalMistBackground.vue";
import LazyImage from "@/Components/LazyImage.vue";
const router = useRouter();

const userStore = useUserStore()
const featureStore = useFeatureStore()

const features = [
    {
        name: "Creatures",
        description: "<p class='leading-tight mb-2'>Effortlessly generate unique and diverse <b class='text-white'>Creatures</b>, from insidious monsters to demons and aberrations straight from your imagination.</p><p class='leading-tight mb-2'>Includes behaviour descriptions, stat blocks, and an illustration, with compatibility for most popular TTRPGs such as D&D and Pathfinder.</p>",
        page: 'creature',
        image: 'api/images/Features/feature_v2_creatures_512.webp',
        action_text: 'Create',
        icon: ['far', 'paw-claws'],
        requires_login: false,
        glow_color: "#FF4500",
    },
    {
        name: "Characters",
        description: "<p class='leading-tight mb-2'>An AI-powered <b class='text-white'>text-to-character</b> service for creating diverse and engaging <b class='text-white'>Characters</b> of any race or class.</p><p class='leading-tight mb-2'>Includes detailed backstories, character traits, and an illustration, with four distinct art styles.</p>",
        page: 'character',
        image: 'api/images/Features/feature_v2_characters_512.webp',
        action_text: 'Create',
        icon: ['fad', 'users'],
        requires_login: false,
        glow_color: "#40E0D0",
    },
    {
        name: "Equipment",
        description: "<p class='leading-tight mb-2'>An AI-powered <b class='text-white'>text-to-equipment</b> service for creating unique <b class='text-white'>weapons</b>, <b class='text-white'>armor</b>, and <b class='text-white'>magical items</b> to enhance your adventures.</p><p class='leading-tight mb-2'>Includes detailed descriptions, stats, and illustrations, with support for various TTRPG systems.</p>",
        page: 'equipment-stash',
        image: 'api/images/Features/feature_equipment_512.webp',
        action_text: 'Create',
        icon: ['far', 'sword'],
        requires_login: false,
        glow_color: "#FF4500",
    },
]

const data = reactive({
    displayLogin: false,
    displayRegister: false,
    authCredentials: {
        email: '',
        password: '',
    },
    shouldRouteToPage: false,
    displayEmailPreferences: false,
    displayNetPromoterScore: false,
})

function maybeRoute(page) {
    if (userStore.isLoggedIn()) {
        data.shouldRouteToPage = null
        route(page)
    } else {
        data.displayRegister = true
        data.shouldRouteToPage = page
    }
}

function route(page) {
    data.shouldRouteToPage = null
    router.push({ name: page })
}

function didAuthenticate() {
    data.displayLogin = false;
    data.displayRegister = false;
    if (data.shouldRouteToPage) {
        route(data.shouldRouteToPage)
    }
}

function handleSwitchToRegister(creds) {
    if (creds) {
        data.authCredentials.email = creds.email || '';
        data.authCredentials.password = creds.password || '';
    }
    data.displayLogin = false;
    data.displayRegister = true;
}

function handleSwitchToLogin(creds) {
    if (creds) {
        data.authCredentials.email = creds.email || '';
        data.authCredentials.password = creds.password || '';
    }
    data.displayRegister = false;
    data.displayLogin = true;
}

function displaySignup() {
    if (userStore.isLoggedIn()) {
        data.shouldRouteToPage = null
        route('pricing')
    } else {
        data.displayRegister = true
    }
}

function routeToAccount() {
    route('account')
}

const isLoggedIn = computed(() => {
    return userStore.userId
})

const homepageVideoUrl = import.meta.env.VITE_HOMEPAGE_VIDEO_URL

watch(
    () => featureStore.getAlerts(),
    async alerts => {
        if (alerts?.email_preferences) {
            data.displayEmailPreferences = true
        }    const discordUrl = import.meta.env.VITE_DISCORD_URL
        if (alerts?.net_promoter_score) {
            console.log('displaying net promoter score')
            data.displayNetPromoterScore = true
        }
    },
    { immediate: true }
)

</script>

<template>
    <title>Tavern of Azoth - The Ultimate AI Driven Generator for Characters, Creatures, Equipment, and Merchants</title>
    <meta name="description" content="Tavern of Azoth - Generation tools for character, creatures, equipment, and merchants. Create your unique content now!"/>
    <div class="bg-gray-900 py-8 wrapper">
        <LoginModal v-if="data.displayLogin" v-show="data.displayLogin" :initial-email="data.authCredentials.email" :initial-password="data.authCredentials.password" @close="data.displayLogin = false" @register="handleSwitchToRegister" @login-success="didAuthenticate"/>
        <RegistrationModal v-if="data.displayRegister" v-show="data.displayRegister" :initial-email="data.authCredentials.email" :initial-password="data.authCredentials.password" @close="data.displayRegister = false" @login="handleSwitchToLogin" @registration-success="didAuthenticate"/>
        <EmailPreferencesModal v-if="data.displayEmailPreferences" v-show="data.displayEmailPreferences" @close="data.displayEmailPreferences = false" />
        <NetPromoterModal v-if="data.displayNetPromoterScore" v-show="data.displayNetPromoterScore" @close="data.displayNetPromoterScore = false" />
<!--        <MagicalMistBackground />-->

        <div class="mx-auto sm:mt-0 mt-0  flex flex-col items-center p-0 sm:p-8 max-w-7xl z-10">
            <div class="mx-auto items-center text-center">
                <h1 class="text-2xl sm:text-5xl px-0 font-bold tracking-tight text-white">Tavern of Azoth</h1>
                <h2 class="text-lg sm:text-2xl px-0 py-2 tracking-tight text-gray-200">TTRPG Tools to Augment Your Campaigns</h2>
                <div v-if="!isLoggedIn" class="mt-6">
                    <button @click="displaySignup" class="bg-orange-700 hover:bg-orange-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                        Get Started Free
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-x-8 gap-y-8 max-w-none sm:mt-12 mt-8 w-full">
                <article v-for="(feature, index) in features" :key="feature.name" class="bg-gray-800 grid grid-cols-3 items-start rounded-md">
                    <div class="col-span-3">
                        <p class="text-sm font-bold text-white bg-gray-750 p-2 border-b-1 border-gray-500">About {{ feature.name }}</p>
                    </div>
                    <div class="relative">
                        <div style="padding-bottom: 100%;" class="w-full h-full"></div>
                        <LazyImage :src="feature.image" :alt="feature.name" :priority="index === 0" />
                    </div>

                    <div class="col-span-2 text-left order-1 flex flex-col justify-between h-full p-2 sm:p-4">
                        <div>
                            <h3 class="text-base md:text-2xl font-semibold text-white mb-2" v-html="feature.name"/>
                            <div class="mx-auto text-sm md:text-lg flex flex-auto flex-col text-gray-300">
                                <p class="flex-auto leading-4 md:leading-6" v-html="feature.description" />
                            </div>
                        </div>
                        <div class="self-end mt-2 md:mt-10 sm:w-auto w-full">
                            <PrimaryButton
                                @click="feature.requires_login ? maybeRoute(feature.page) : route(feature.page)"
                                class="md:!text-base !p-2 !py-1 text-white w-full flex justify-center items-center"
                                :style="{ backgroundColor: '#C2410C' }"
                            >
                                <div class="flex items-center">
                                    <FontAwesomeIcon :icon="feature.icon" class="mr-2" size="lg" style="cursor: pointer; color: #ffffff"/>
                                    {{ feature.action_text }}
                                </div>
                            </PrimaryButton>
                        </div>
                    </div>
                </article>
            </div>
        </div>


    </div>
</template>

<style scoped>
.wrapper {
    min-height: 100vh;
    margin: 0 auto -80px; /* the bottom margin is the negative value of the footer's height */
}
</style>
